/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/no-extraneous-dependencies */
import { useEffect, useState, useRef } from "react";
import {
  Button,
  Combobox,
  DatePicker,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Textarea,
} from "@repo/ui";
import { useForm } from "react-hook-form";
import { Popover, PopoverTrigger, PopoverContent } from "@repo/ui";
import { useSubmitReport } from "services/reports";
import { useUser } from "hooks/use-user";
import {
  useLookupActivityTypes,
  useLookupAllMachinesServicePoints,
  useLookupServiceTicketActivityTypes,
} from "services/lookups";
import KendoMultiselect from "ui/multiselect";
import { getLocalISODateString } from "utils/date-utils";

const SERVICE_TICKET_STATUS_OPTIONS = [
  { text: "All Tickets", value: "OpenDate" },
  { text: "Rejected Tickets", value: "RejectedDate" },
  { text: "Closed Tickets", value: "ClosedDate" },
  { text: "Cancelled Tickets", value: "CancelledDate" },
];

const TASK_STATUS_OPTIONS = [
  { text: "All Tasks", value: "OpenDate" },
  { text: "Closed Tasks", value: "ClosedDate" },
  { text: "Cancelled Tasks", value: "CancelledDate" },
];

export default function GeneratedReportsFilter({
  customersList = { data: [] },
  cashCentersList = { data: [] },
  reportType,
  onSuccess,
}) {
  const user = useUser();
  const [assets, setAssets] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const assetFetchRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutateAsync: getLocations } = useLookupAllMachinesServicePoints();
  const { mutate: getActivityTypes, data: activityTypesRes } =
    useLookupActivityTypes();

  const {
    mutate: getServiceTicketActivityTypes,
    data: serviceTicketActivityTypesRes,
  } = useLookupServiceTicketActivityTypes();

  const resolvedDateFilters =
    reportType === "ServiceTicketsReport"
      ? SERVICE_TICKET_STATUS_OPTIONS
      : TASK_STATUS_OPTIONS;

  const {
    mutate: submitReport,
    isSuccess,
    isError,
    reset: resetSubmit,
  } = useSubmitReport();

  const defaultValues = {
    customerId: [],
    assetId: [],
    activityType: [],
    cashCenterId: "",
    dateFilter: "",
    fromDate: undefined,
    toDate: undefined,
    remarks: "",
    toEmail: user.Email,
    notifyOnReportCompletion: false,
  };

  const form = useForm({
    defaultValues,
    mode: "onTouched",
  });

  const {
    watch,
    setValue,
    getValues,
    reset,
    trigger,
    clearErrors,
    formState: { errors },
  } = form;

  const verificationRules = {
    dateFilter: { required: "Please select Date Filter" },
    fromDate: { required: "Please select From Date" },
    toDate: { required: "Please select To Date" },
  };

  useEffect(() => {
    form.register("dateFilter", verificationRules.dateFilter);
    form.register("fromDate", verificationRules.fromDate);
    form.register("toDate", verificationRules.toDate);
  }, []);

  useEffect(() => {
    if (reportType === "ServiceTicketsReport") {
      getActivityTypes();
    } else {
      getServiceTicketActivityTypes();
    }

    setValue("activityType", []);
  }, [reportType]);

  useEffect(() => {
    const customers = watch("customerId");
    setAssets([]);
    setValue("assetId", []);

    if (!customers?.length) return;

    if (assetFetchRef.current) clearTimeout(assetFetchRef.current);

    assetFetchRef.current = setTimeout(async () => {
      try {
        const responses = await Promise.all(
          customers.map((c) => getLocations(c.customerId)),
        );
        const mergedAssets = responses.map((r) => r?.data || []).flat();
        setAssets(mergedAssets);
      } catch {
        setAssets([]);
      }
    }, 500);

    return () => {
      if (assetFetchRef.current) clearTimeout(assetFetchRef.current);
      assetFetchRef.current = null;
    };
  }, [watch("customerId")]);

  useEffect(() => {
    const selectedAssets = watch("assetId");
    if (!selectedAssets?.length) return;

    const validIds = new Set(assets.map((a) => a.servicePointId));
    const cleaned = selectedAssets.filter((a) =>
      validIds.has(a.servicePointId),
    );

    if (cleaned.length !== selectedAssets.length) {
      setValue("assetId", cleaned);
    }
  }, [assets]);

  const sendReport = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setIsDialogOpen(false);

    const data = getValues();
    const selectedDateFilter = resolvedDateFilters.find(
      (d) => d.value === data.dateFilter,
    );

    const payload = {
      reportType,
      parameters: {
        CashCenter: data.cashCenterId
          ? [
              {
                value: data.cashCenterId,
                text: cashCentersList.data.find(
                  (c) => c.cashCenterId === data.cashCenterId,
                )?.name,
              },
            ]
          : [],
        Customer: data.customerId.map((c) => ({
          value: c.customerId,
          text: c.name,
        })),
        Asset: data.assetId.map((a) => ({
          value: a.servicePointId,
          text: a.locationId,
        })),
        ActivityType: data.activityType.map((a) => ({
          value: a.description,
          text: a.description,
        })),
        DateFilterCriteria: selectedDateFilter
          ? [
              {
                value: selectedDateFilter.value,
                text: selectedDateFilter.text,
              },
            ]
          : [],
      },
      fromDate: getLocalISODateString(data.fromDate),
      toDate: getLocalISODateString(data.toDate),
      toEmail: data.toEmail,
      message: data.remarks,
      notifyOnReportCompletion: data.notifyOnReportCompletion,
    };

    submitReport(payload);
  };

  useEffect(() => {
    if (isSuccess || isError) {
      setIsSubmitting(false);
    }

    if (isSuccess) {
      onSuccess?.();
      resetSubmit();
      reset(defaultValues, {
        keepErrors: false,
        keepDirty: false,
        keepTouched: false,
      });
      clearErrors();
      setAssets([]);
    }
  }, [isSuccess, isError]);

  const dateFilter = watch("dateFilter");
  const fromDate = watch("fromDate");
  const toDate = watch("toDate");

  const isFormValid = !!dateFilter && !!fromDate && !!toDate;
  const activityTypesData =
    reportType === "ServiceTicketsReport"
      ? activityTypesRes?.data || []
      : serviceTicketActivityTypesRes?.data || [];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="secondary">Request Report</Button>
      </PopoverTrigger>

      <PopoverContent className="w-[720px] p-4">
        <div className="grid grid-cols-2 gap-4">
          <KendoMultiselect
            data={customersList.data}
            textField="name"
            dataItemKey="customerId"
            value={watch("customerId")}
            onChange={(e) => setValue("customerId", e.target.value)}
            placeholder="Customers"
          />

          <Combobox
            placeholder="Select Cash Center"
            data={[
              { label: "Select Cash Center", value: "" },
              ...(cashCentersList.data || []).map((c) => ({
                label: c.name,
                value: c.cashCenterId,
              })),
            ]}
            value={watch("cashCenterId")}
            setValue={(value) => setValue("cashCenterId", value)}
          />

          <KendoMultiselect
            data={assets}
            textField="locationId"
            dataItemKey="servicePointId"
            value={watch("assetId")}
            onChange={(e) => setValue("assetId", e.target.value)}
            placeholder="Assets"
          />

          <KendoMultiselect
            data={activityTypesData}
            textField="description"
            dataItemKey="description"
            value={watch("activityType")}
            onChange={(e) => setValue("activityType", e.target.value)}
            placeholder="Activity Types"
          />

          <div className="flex flex-col col-span-2">
            <Combobox
              placeholder="Select Date Filter"
              isRequired
              data={[
                { label: "Select Date Filter", value: "" },
                ...resolvedDateFilters.map((d) => ({
                  label: d.text,
                  value: d.value,
                })),
              ]}
              value={watch("dateFilter")}
              setValue={(value) =>
                setValue("dateFilter", value, { shouldValidate: true })
              }
            />
            {errors?.dateFilter ? (
              <p className="text-destructive text-xs mt-1">
                {errors.dateFilter.message}
              </p>
            ) : null}
          </div>

          <div className="col-span-2 flex gap-4">
            <div className="flex-1 flex flex-col">
              <label className="text-sm font-medium mb-1">
                From <span className="text-destructive">*</span>
              </label>
              <DatePicker
                selectedDate={watch("fromDate")}
                onSelectDate={(d) =>
                  setValue("fromDate", d, { shouldValidate: true })
                }
              />
              {errors?.fromDate ? (
                <p className="text-destructive text-xs mt-1">
                  {errors.fromDate.message}
                </p>
              ) : null}
            </div>

            <div className="flex-1 flex flex-col">
              <label className="text-sm font-medium mb-1">
                To <span className="text-destructive">*</span>
              </label>
              <DatePicker
                selectedDate={watch("toDate")}
                onSelectDate={(d) =>
                  setValue("toDate", d, { shouldValidate: true })
                }
              />
              {errors?.toDate ? (
                <p className="text-destructive text-xs mt-1">
                  {errors.toDate.message}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-end mt-4 gap-2">
          {/* Clear */}
          <Button
            variant="ghost"
            type="button"
            onClick={() => {
              reset(defaultValues, {
                keepErrors: false,
                keepDirty: false,
                keepTouched: false,
              });
              clearErrors();
              setAssets([]);
            }}
          >
            Clear
          </Button>

          {/* Submit (disabled if mandatory fields empty) */}
          <Button
            variant="secondary"
            type="button"
            disabled={!isFormValid}
            onClick={() => setIsDialogOpen(true)}
          >
            Submit
          </Button>
        </div>

        {/* Email Confirmation Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Email Confirmation</DialogTitle>
            </DialogHeader>

            <div className="rounded text-sm mb-4">
              <p>
                Report generation may take time based on the selected criteria,
                once the report is generated an email notification will be sent.
              </p>
              <p className="mt-2">
                Click <strong>Refresh</strong> button to check status of your
                request.
              </p>
            </div>

            <div className="mb-3">
              <label className="text-sm font-medium mb-1 block">Email</label>
              <input
                type="email"
                value={watch("toEmail")}
                disabled
                className="w-full border rounded p-2 bg-gray-100 cursor-not-allowed"
              />
            </div>

            <div>
              <Textarea
                placeholder="Remarks"
                maxLength={300}
                value={watch("remarks")}
                onChange={(e) => setValue("remarks", e.target.value)}
              />
              <p className="text-xs text-right mt-1">
                {watch("remarks")?.length || 0} / 300
              </p>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input
                type="checkbox"
                id="emailNotificationRequired"
                checked={watch("notifyOnReportCompletion")}
                onChange={(e) =>
                  setValue("notifyOnReportCompletion", e.target.checked)
                }
                className="h-4 w-4 cursor-pointer"
              />
              <label
                htmlFor="emailNotificationRequired"
                className="text-sm cursor-pointer"
              >
                Email Notification Required
              </label>
            </div>

            <div className="w-full mt-6 text-sm">
              <p>
                <strong>Disclaimer : </strong>
              </p>
              <p>Generated report will be accessible for 7 days only.</p>
            </div>

            <div className="flex justify-end mt-4">
              <Button
                variant="secondary"
                type="button"
                disabled={isSubmitting}
                onClick={sendReport}
              >
                Confirm
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </PopoverContent>
    </Popover>
  );
}
